import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";
import type {
  AdminVideoTargetOptionDto,
  AdminVideosResponseDto,
  VideoTargetType,
  VideoUploadSessionResponseDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  completeAdminVideoUpload,
  createAdminVideo,
  createAdminVideoUploadSession,
  fetchAdminVideoTargetOptions,
  fetchAdminVideos,
  hideAdminVideo,
  patchAdminVideo,
  republishAdminVideo
} from "../../shared/api/video";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { getEnv } from "../../shared/lib/env";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus, describeTenantStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/events", label: "イベント" }
];

function toDatetimeLocalValue(value: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoString(value: string): string | null {
  if (!value) {
    return null;
  }

  return new Date(value).toISOString();
}

type EditState = {
  title: string;
  description: string;
  publishFrom: string;
  publishTo: string;
};

type UploadUiState = {
  status: "idle" | "sessionCreated" | "uploading" | "uploaded" | "failed";
  message: string;
};

type TargetOptionsByType = Record<VideoTargetType, AdminVideoTargetOptionDto[]>;

const env = getEnv();

function isLikelyUploadNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message === "Load failed"
    || error.message === "Failed to fetch"
    || error.message.includes("Network request failed");
}

function buildUploadFailureMessage(error: unknown, file: File | null): string {
  if (isLikelyUploadNetworkError(error)) {
    const fileHint =
      file?.type === "video/quicktime" || file?.name.toLowerCase().endsWith(".mov")
        ? "MOV 自体は backend で許可しています。"
        : "ファイル形式自体よりも、ブラウザから Cloud Storage への PUT アップロード失敗の可能性が高いです。";

    return `${fileHint} iPhone Safari では Cloud Storage resumable upload が CORS / cross-origin 制約で "Load failed" になることがあります。review bucket で https://review.madamuhlesson.com からの PUT / OPTIONS を許可しているか確認してください。`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "動画アップロードに失敗しました。";
}

function buildRecoveredUploadMessage(file: File | null): string {
  const fileLabel = file?.name ?? "選択中のファイル";
  return `${fileLabel} のアップロード応答はブラウザで確認できませんでしたが、Cloud Storage 上のオブジェクトを確認できたため完了 API を継続しました。`;
}

function buildEditStateMap(videos: AdminVideosResponseDto | null): Record<string, EditState> {
  if (!videos) {
    return {};
  }

  return Object.fromEntries(
    videos.items.map((video) => [
      video.videoId,
      {
        title: video.title ?? "",
        description: video.description ?? "",
        publishFrom: toDatetimeLocalValue(video.publishFrom),
        publishTo: toDatetimeLocalValue(video.publishTo)
      }
    ])
  );
}

export function AdminVideosPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [videos, setVideos] = useState<AdminVideosResponseDto | null>(null);
  const [videoEdits, setVideoEdits] = useState<Record<string, EditState>>({});
  const [uploadSessions, setUploadSessions] = useState<Record<string, VideoUploadSessionResponseDto>>({});
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File | null>>({});
  const [uploadStates, setUploadStates] = useState<Record<string, UploadUiState>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [actionState, setActionState] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [videoPurpose, setVideoPurpose] = useState("lessonSupport");
  const [targetType, setTargetType] = useState<VideoTargetType>("lesson");
  const [targetId, setTargetId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [publishFrom, setPublishFrom] = useState("");
  const [publishTo, setPublishTo] = useState("");
  const [targetOptions, setTargetOptions] = useState<TargetOptionsByType>({
    lesson: [],
    occurrence: [],
    event: []
  });
  const [targetOptionsState, setTargetOptionsState] = useState<"idle" | "loading" | "error">("idle");
  const [targetOptionsError, setTargetOptionsError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const selectedTargetOption = targetOptions[targetType].find((option) => option.targetId === targetId) ?? null;

  async function finalizeUploadedVideo(
    tenantSlugValue: string,
    videoId: string,
    uploadSessionToken: string,
    file: File
  ) {
    return await completeAdminVideoUpload(tenantSlugValue, videoId, {
      uploadSessionToken,
      originalFileName: file.name,
      mimeType: file.type || "video/mp4",
      fileSizeBytes: file.size
    });
  }

  async function refreshVideos() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const nextVideos = await fetchAdminVideos(tenantSlug);
      setVideos(nextVideos);
      setVideoEdits(buildEditStateMap(nextVideos));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "動画一覧の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshVideos();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let disposed = false;

    async function refreshTargetOptions() {
      setTargetOptionsState("loading");
      setTargetOptionsError(null);

      try {
        const [lessonOptions, occurrenceOptions, eventOptions] = await Promise.all([
          fetchAdminVideoTargetOptions(tenantSlug, "lesson"),
          fetchAdminVideoTargetOptions(tenantSlug, "occurrence"),
          fetchAdminVideoTargetOptions(tenantSlug, "event")
        ]);

        if (disposed) {
          return;
        }

        setTargetOptions({
          lesson: lessonOptions.items,
          occurrence: occurrenceOptions.items,
          event: eventOptions.items
        });
        setTargetOptionsState("idle");
      } catch (error) {
        if (disposed) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setTargetOptionsState("error");
        setTargetOptionsError(getProtectedApiErrorMessage(error, "動画対象候補の取得に失敗しました"));
      }
    }

    void refreshTargetOptions();

    return () => {
      disposed = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  useEffect(() => {
    const options = targetOptions[targetType];

    if (options.length === 0) {
      return;
    }

    const targetExists = options.some((option) => option.targetId === targetId);

    if (!targetId || !targetExists) {
      setTargetId(options[0]?.targetId ?? "");
    }
  }, [targetId, targetOptions, targetType]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionState("create");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await createAdminVideo(tenantSlug, {
        videoPurpose: videoPurpose === "replacement" ? "replacement" : videoPurpose === "eventSupport" ? "eventSupport" : "lessonSupport",
        targetType,
        title: title || null,
        description: description || null,
        publishFrom: toIsoString(publishFrom),
        publishTo: toIsoString(publishTo),
        lessonId: targetType === "lesson" ? targetId.trim() : null,
        occurrenceId: targetType === "occurrence" ? targetId.trim() : null,
        eventId: targetType === "event" ? targetId.trim() : null
      });

      setTitle("");
      setDescription("");
      setPublishFrom("");
      setPublishTo("");
      setFeedbackMessage("video draft を作成しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "video create に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleUploadSession(videoId: string) {
    setActionState(`upload:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextUploadSession = await createAdminVideoUploadSession(tenantSlug, videoId);
      setUploadSessions((current) => ({
        ...current,
        [videoId]: nextUploadSession
      }));
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "sessionCreated",
          message: "アップロードセッションを作成しました。ファイルを選択してアップロードできます。"
        }
      }));
      setFeedbackMessage("アップロードセッションを作成しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "upload-session の作成に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleComplete(videoId: string) {
    const uploadSession = uploadSessions[videoId];

    if (!uploadSession) {
      setLoadError("先に upload-session を発行してください。");
      return;
    }

    setActionState(`complete:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await completeAdminVideoUpload(tenantSlug, videoId, {
        uploadSessionToken: uploadSession.uploadSessionToken,
        originalFileName: `${videoId}.mp4`,
        mimeType: "video/mp4",
        fileSizeBytes: 20_000_000,
        durationSeconds: 180
      });

      setUploadSessions((current) => {
        const next = { ...current };
        delete next[videoId];
        return next;
      });
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "uploaded",
          message: "アップロード完了処理を実行しました。"
        }
      }));
      setFeedbackMessage("upload complete を実行しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "upload complete に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handlePatch(videoId: string) {
    const editState = videoEdits[videoId];

    if (!editState) {
      return;
    }

    setActionState(`patch:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await patchAdminVideo(tenantSlug, videoId, {
        title: editState.title || null,
        description: editState.description || null,
        publishFrom: toIsoString(editState.publishFrom),
        publishTo: toIsoString(editState.publishTo)
      });
      setFeedbackMessage("video metadata を保存しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "video patch に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleHide(videoId: string) {
    setActionState(`hide:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await hideAdminVideo(tenantSlug, videoId);
      setFeedbackMessage("video を hidden にしました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "video hide に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  async function handleRepublish(videoId: string) {
    setActionState(`republish:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await republishAdminVideo(tenantSlug, videoId);
      setFeedbackMessage("video を republish しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "video republish に失敗しました"));
    } finally {
      setActionState(null);
    }
  }

  function handleFileChange(videoId: string, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;

    setSelectedFiles((current) => ({
      ...current,
      [videoId]: file
    }));
    setUploadStates((current) => ({
      ...current,
      [videoId]: file
        ? {
            status: uploadSessions[videoId] ? "sessionCreated" : "idle",
            message: uploadSessions[videoId]
              ? `${file.name} を選択しました。アップロードを実行できます。`
              : "先にアップロードセッションを作成してください。"
          }
        : {
            status: uploadSessions[videoId] ? "sessionCreated" : "idle",
            message: uploadSessions[videoId] ? "ファイル未選択です。" : "アップロードセッション未作成です。"
          }
    }));
  }

  async function uploadFileToSession(uploadUrl: string, file: File): Promise<void> {
    if (env.enableMock && uploadUrl.includes("upload.mock.local")) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open("PUT", uploadUrl);
      xhr.responseType = "text";
      xhr.timeout = 120_000;
      xhr.withCredentials = false;
      xhr.setRequestHeader("Content-Type", file.type || "video/mp4");

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }

        reject(
          new Error(
            `PUT upload failed (${xhr.status} ${xhr.statusText})${xhr.responseText ? `: ${String(xhr.responseText).slice(0, 200)}` : ""}`
          )
        );
      };

      xhr.onerror = () => {
        reject(new Error("Load failed"));
      };

      xhr.onabort = () => {
        reject(new Error("Upload aborted"));
      };

      xhr.ontimeout = () => {
        reject(new Error("Upload timed out"));
      };

      xhr.send(file);
    });
  }

  async function handleFileUpload(videoId: string) {
    const uploadSession = uploadSessions[videoId];
    const file = selectedFiles[videoId];

    if (!uploadSession) {
      setLoadError("先に upload-session を発行してください。");
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "failed",
          message: "アップロードセッションがありません。"
        }
      }));
      return;
    }

    if (!file) {
      setLoadError("動画ファイルを選択してください。");
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "failed",
          message: "ファイル未選択です。"
        }
      }));
      return;
    }

    setActionState(`upload-file:${videoId}`);
    setLoadError(null);
    setFeedbackMessage(null);
    setUploadStates((current) => ({
      ...current,
      [videoId]: {
        status: "uploading",
        message: `${file.name} をアップロードしています。`
      }
    }));

    try {
      await uploadFileToSession(uploadSession.uploadUrl, file);
      await finalizeUploadedVideo(tenantSlug, videoId, uploadSession.uploadSessionToken, file);

      setUploadSessions((current) => {
        const next = { ...current };
        delete next[videoId];
        return next;
      });
      setSelectedFiles((current) => ({
        ...current,
        [videoId]: null
      }));
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "uploaded",
          message: env.enableMock && uploadSession.uploadUrl.includes("upload.mock.local")
            ? "レビュー用アップロードを完了し、完了 API を実行しました。"
            : "ファイルアップロードと完了処理を実行しました。"
        }
      }));
      setFeedbackMessage("ファイルアップロードと完了処理を実行しました。");
      await refreshVideos();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      if (isLikelyUploadNetworkError(error)) {
        try {
          await finalizeUploadedVideo(tenantSlug, videoId, uploadSession.uploadSessionToken, file);

          setUploadSessions((current) => {
            const next = { ...current };
            delete next[videoId];
            return next;
          });
          setSelectedFiles((current) => ({
            ...current,
            [videoId]: null
          }));
          setUploadStates((current) => ({
            ...current,
            [videoId]: {
              status: "uploaded",
              message: buildRecoveredUploadMessage(file)
            }
          }));
          setFeedbackMessage(buildRecoveredUploadMessage(file));
          await refreshVideos();
          return;
        } catch (completeError) {
          if (isUnauthenticatedApiError(completeError)) {
            navigate(`/t/${tenantSlug}/entry`, { replace: true });
            return;
          }

          const uploadMessage = buildUploadFailureMessage(error, file);
          const completeMessage = getProtectedApiErrorMessage(completeError, "アップロード完了処理にも失敗しました");
          const combinedMessage = `${uploadMessage} 完了処理結果: ${completeMessage}`;

          setLoadError(combinedMessage);
          setUploadStates((current) => ({
            ...current,
            [videoId]: {
              status: "failed",
              message: combinedMessage
            }
          }));
          return;
        }
      }

      const message = buildUploadFailureMessage(error, file);

      setLoadError(message);
      setUploadStates((current) => ({
        ...current,
        [videoId]: {
          status: "failed",
          message
        }
      }));
    } finally {
      setActionState(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="動画"
      description="下書き作成、アップロード準備、アップロード完了、自動公開の流れを確認する画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>動画一覧を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {targetOptionsError ? <p>{targetOptionsError}</p> : null}

        <form onSubmit={handleCreate} style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>下書きを作成</h2>
          <label style={{ display: "grid", gap: 4 }}>
            <span>動画用途</span>
            <select value={videoPurpose} onChange={(event) => setVideoPurpose(event.target.value)}>
              <option value="lessonSupport">{describeTenantStatus("lessonSupport", tenantLabels)}</option>
              <option value="replacement">{describeStatus("replacement")}</option>
              <option value="eventSupport">{describeTenantStatus("eventSupport", tenantLabels)}</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>対象種別</span>
            <select value={targetType} onChange={(event: ChangeEvent<HTMLSelectElement>) => setTargetType(event.target.value as VideoTargetType)}>
              <option value="lesson">{describeTenantStatus("lesson", tenantLabels)}</option>
              <option value="occurrence">{describeTenantStatus("occurrence", tenantLabels)}</option>
              <option value="event">{describeTenantStatus("event", tenantLabels)}</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>対象</span>
            {targetOptions[targetType].length > 0 ? (
              <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                {targetOptions[targetType].map((option) => (
                  <option key={`${option.targetType}:${option.targetId}`} value={option.targetId}>
                    {option.label}
                    {option.subtitle ? ` / ${option.subtitle}` : ""}
                    {option.status ? ` / ステータス: ${describeStatus(option.status)}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input value={targetId} onChange={(event) => setTargetId(event.target.value)} />
            )}
          </label>
          {targetOptionsState === "loading" ? <div style={{ fontSize: 13, opacity: 0.8 }}>対象候補を読み込んでいます。</div> : null}
          {targetOptions[targetType].length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              選択可能な対象がまだありません。必要なら対象IDを手入力してください。
            </div>
          ) : null}
          {selectedTargetOption ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {selectedTargetOption.subtitle ?? "対象"}
              {selectedTargetOption.status
                ? ` / ステータス: ${describeStatus(selectedTargetOption.status)}`
                : ""}
            </div>
          ) : null}
          <label style={{ display: "grid", gap: 4 }}>
            <span>タイトル</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>説明</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>公開開始</span>
            <input type="datetime-local" value={publishFrom} onChange={(event) => setPublishFrom(event.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>公開終了</span>
            <input type="datetime-local" value={publishTo} onChange={(event) => setPublishTo(event.target.value)} />
          </label>
          <button type="submit" disabled={actionState === "create" || targetOptionsState === "loading" || targetId.trim().length === 0}>
            {actionState === "create" ? "作成中..." : "動画下書きを作成"}
          </button>
              {env.enableMock ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              レビュー用対象ID: `lesson_regular_kids_hiphop`, `occ_demo_family_change_001`, `occ_demo_ben_cancelled_001`, `event_demo_showcase_001`
            </div>
          ) : null}
        </form>

        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>動画一覧</h2>
          {videos?.items.length === 0 ? <p style={{ margin: 0 }}>動画はありません。</p> : null}

          {videos?.items.map((video) => {
            const uploadSession = uploadSessions[video.videoId];
            const selectedFile = selectedFiles[video.videoId];
            const uploadState = uploadStates[video.videoId];
            const uploadSessionLabel =
              env.enableMock && uploadSession?.uploadUrl.includes("upload.mock.local")
                ? "レビュー用アップロード準備"
                : "アップロード準備";

            return (
              <article
                key={video.videoId}
                style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 10 }}
              >
                <div style={{ display: "grid", gap: 4 }}>
                  <strong>{video.title ?? "タイトル未設定"}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    {describeTenantStatus(video.videoPurpose, tenantLabels)} / {describeTenantStatus(video.targetType, tenantLabels)} / {video.targetLabel}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    アップロード状態: {describeStatus(video.uploadStatus)} / 公開状態: {describeStatus(video.publishStatus)}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    公開開始: {video.publishFrom ?? "未設定"} / 公開終了: {video.publishTo ?? "未設定"}
                  </div>
                  {video.publishErrorCodes.length > 0 ? (
                    <div style={{ fontSize: 14 }}>公開エラー: {video.publishErrorCodes.join(", ")}</div>
                  ) : null}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => void handleUploadSession(video.videoId)}
                    disabled={actionState === `upload:${video.videoId}`}
                  >
                    {actionState === `upload:${video.videoId}` ? "発行中..." : "アップロード準備"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleComplete(video.videoId)}
                    disabled={actionState === `complete:${video.videoId}` || actionState === `upload-file:${video.videoId}`}
                  >
                    {actionState === `complete:${video.videoId}` ? "完了中..." : "アップロード完了"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleHide(video.videoId)}
                    disabled={actionState === `hide:${video.videoId}`}
                  >
                    {actionState === `hide:${video.videoId}` ? "処理中..." : "非表示にする"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRepublish(video.videoId)}
                    disabled={actionState === `republish:${video.videoId}`}
                  >
                    {actionState === `republish:${video.videoId}` ? "処理中..." : "再公開"}
                  </button>
                </div>

                {uploadSession ? (
                  <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                    <strong>{uploadSessionLabel}</strong>
                    <span>{uploadSession.uploadUrl}</span>
                    <span>トークン: {uploadSession.uploadSessionToken}</span>
                    <span>有効期限: {uploadSession.expiresAt}</span>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>動画ファイル</span>
                      <input
                        type="file"
                        accept=".mp4,.mov,video/mp4,video/quicktime,video/*"
                        onChange={(event) => handleFileChange(video.videoId, event)}
                      />
                    </label>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {selectedFile ? `選択中: ${selectedFile.name} (${selectedFile.type || "video/mp4"})` : "ファイル未選択"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {uploadState?.message ?? "アップロード準備ができました"}
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleFileUpload(video.videoId)}
                      disabled={actionState === `upload-file:${video.videoId}`}
                    >
                      {actionState === `upload-file:${video.videoId}` ? "アップロード中..." : "ファイルをアップロード"}
                    </button>
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>タイトル</span>
                    <input
                      value={videoEdits[video.videoId]?.title ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            title: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>説明</span>
                    <textarea
                      value={videoEdits[video.videoId]?.description ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            description: event.target.value
                          }
                        }))
                      }
                      rows={3}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>公開開始</span>
                    <input
                      type="datetime-local"
                      value={videoEdits[video.videoId]?.publishFrom ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            publishFrom: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>公開終了</span>
                    <input
                      type="datetime-local"
                      value={videoEdits[video.videoId]?.publishTo ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            publishTo: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => void handlePatch(video.videoId)}
                    disabled={actionState === `patch:${video.videoId}`}
                  >
                    {actionState === `patch:${video.videoId}` ? "保存中..." : "メタデータを更新"}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </OperatorPageScaffold>
  );
}
